import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Archive, ArchiveRestore } from "lucide-react";
import { STAGES, stageStatusColor } from "@/lib/constants";
import GenieEmpty from "@/components/GenieEmpty";
import GenieLoader from "@/components/GenieLoader";

export default function ProjectsList() {
    const [items, setItems] = useState(null);  // null = loading, [] = empty
    const [showArchived, setShowArchived] = useState(false);

    const load = useCallback(() => {
        setItems(null);
        const url = showArchived ? "/projects?include_archived=true" : "/projects";
        api.get(url).then(({ data }) => setItems(data));
    }, [showArchived]);

    useEffect(() => { load(); }, [load]);

    return (
        <div className="p-6 sm:p-8 lg:p-10 max-w-7xl mx-auto" data-testid="projects-page">
            <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
                <div>
                    <div className="label-micro mb-2">All Briefs</div>
                    <h1 className="font-display text-4xl sm:text-5xl font-medium tracking-tight">Projects</h1>
                </div>
                <div className="flex items-center gap-3">
                    <label
                        className="flex items-center gap-2 text-xs font-mono text-zinc-400 cursor-pointer select-none"
                        data-testid="projects-show-archived-toggle"
                    >
                        <input
                            type="checkbox"
                            checked={showArchived}
                            onChange={(e) => setShowArchived(e.target.checked)}
                            className="accent-amber-500"
                        />
                        Show archived
                    </label>
                    <Link to="/projects/new" data-testid="add-project-btn" className="btn-primary">
                        <Plus className="w-4 h-4" /> New project
                    </Link>
                </div>
            </div>

            {items === null ? (
                <div className="border border-dashed border-zinc-900 rounded-sm">
                    <GenieLoader />
                </div>
            ) : items.length === 0 ? (
                <GenieEmpty
                    title={showArchived ? "No archived projects." : "No Furmais yet."}
                    subtitle={showArchived ? "Untick \"Show archived\" to see your active projects." : "Tell me your Furmais — I'll shape the brief into a campaign."}
                    ctaTo="/projects/new"
                />
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {items.map(p => (
                        <ProjectCard key={p.id} p={p} onChanged={load} />
                    ))}
                </div>
            )}
        </div>
    );
}

/** Project card with a quick-archive (or unarchive) action.
 *  Card body is a Link to the project board; the action button stops
 *  propagation so the click doesn't navigate. */
function ProjectCard({ p, onChanged }) {
    const [busy, setBusy] = useState(false);

    const onAction = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (busy) return;
        const archived = !!p.is_archived;
        if (!archived) {
            const isRunning = (p.auto_pipeline?.status === "running") || p.auto_pipeline?.agent_running;
            const msg = isRunning
                ? "This project's pipeline is still running. Archiving will cancel it and hide the project. Outputs are preserved. Continue?"
                : "Archive this project? It will be hidden from your active dashboard. Outputs are preserved.";
            if (!window.confirm(msg)) return;
        }
        setBusy(true);
        try {
            if (archived) {
                await api.post(`/projects/${p.id}/unarchive`);
                toast.success("Project unarchived.");
            } else {
                await api.post(`/projects/${p.id}/archive`);
                toast.success("Project archived.");
            }
            await onChanged();
        } catch (err) {
            toast.error(err.userMessage || err.message || "Action failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Link
            to={`/projects/${p.id}`}
            data-testid={`project-card-${p.id}`}
            className={`card-base card-hover p-6 relative block ${p.is_archived ? "opacity-70" : ""}`}
        >
            <div className="flex items-center justify-between mb-3 gap-2">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">
                    {p.project_type.replaceAll("_", " ")}
                </div>
                <div className="flex items-center gap-2">
                    {p.is_archived ? (
                        <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-sm border border-amber-500/40 text-amber-300">
                            Archived
                        </span>
                    ) : (
                        <span className={`text-[10px] font-mono px-2 py-0.5 rounded-sm border ${stageStatusColor("pending")}`}>
                            {STAGES.find(x => x.key === p.current_stage)?.label}
                        </span>
                    )}
                    <button
                        type="button"
                        onClick={onAction}
                        disabled={busy}
                        data-testid={p.is_archived ? `project-unarchive-${p.id}` : `project-archive-${p.id}`}
                        title={p.is_archived ? "Restore project" : "Archive project"}
                        className="p-1 rounded-sm border border-zinc-800 text-zinc-400 hover:text-white hover:border-zinc-500 disabled:opacity-50 transition-colors"
                    >
                        {p.is_archived
                            ? <ArchiveRestore className="w-3.5 h-3.5" />
                            : <Archive className="w-3.5 h-3.5" />}
                    </button>
                </div>
            </div>
            <div className="font-display text-xl tracking-tight">{p.title}</div>
            <div className="text-sm text-zinc-400 mt-3 line-clamp-2">{p.brief}</div>
        </Link>
    );
}
